import {
  Box,
  Button,
  Card,
  InputAdornment,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { useAppSelector } from "hooks/use-app-select";
import { useEffect, useState } from "react";
import history from "utils/history";
import {
  checkPasswordStrength,
  handleErrorMessage,
  isPasswordStrong,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { selectAuthenticated } from "redux/authSlice";
import urls from "global/constants/UrlConstants";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { useLocation } from "react-router-dom";
import { updatePwd, verifyTempPassword } from "./AuthService";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { CustomInput } from "global/components";
import { useAuthRouteRedirection } from "layouts/Config";
import viewpaths from "global/constants/ViewPathConstants";
import inkfreeLogo from "assets/images/side_nav_inkfree_logo.svg";
import { mobile, signer, signerAccentBar } from "theme/colors";
import { authRef, refFont } from "./AuthReferenceStyles";

interface CustomProps {
  location: any;
}

const NewPassword = (props: CustomProps) => {
  useTitle(title.RESET_PASSWORD);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const context = new URLSearchParams(useLocation().search).get("context");
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const [user, setUser] = useState<any>();
  const [credentials, setCredentials] = useState({
    new_password: {
      value: "",
      error: "",
    },
    confirm_password: {
      value: "",
      error: "",
    },
  });
  const [visibility, setVisibility] = useState({
    new_password: false,
    confirm_password: false,
  });
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    verifyPwd(context);
  }, []);

  const verifyPwd = async (token: any) => {
    try {
      const userData: any = {
        authToken: token,
      };
      const response: any = await verifyTempPassword(userData);
      setUser(response);
    } catch (error: any) {
      handleErrorMessage(error);
      history.push(viewpaths.landingViewPath);
    }
  };

  const handleValidation = () => {
    let errors = credentials;
    let isValid = true;
    const newPassword = credentials.new_password.value;
    const confirmPassword = credentials.confirm_password.value;
    if (!newPassword && !confirmPassword) {
      errors.new_password.error = "New password is required";
      errors.confirm_password.error = "Confirm password is required";
    }
    if (!newPassword) {
      errors.new_password.error = "New password is required";
      isValid = false;
    }
    if (!confirmPassword) {
      errors.confirm_password.error = "Confirm password is required";
      isValid = false;
    }
    if (newPassword !== confirmPassword) {
      openErrorNotification("New password and confirm password does not match");
      return false;
    }
    if (
      credentials.new_password.value &&
      !isPasswordStrong(credentials.new_password.value)
    ) {
      openErrorNotification("Entered password is not strong");
      isValid = false;
    }
    setCredentials({ ...errors });
    return isValid;
  };

  // const validate = () => {
  //   if (!isTruthy(credentials.new_password)) {
  //     openErrorNotification("Please enter the password");
  //     return false;
  //   }
  //   if (!isTruthy(credentials.new_password)) {
  //     openErrorNotification("Please enter the confirm password");
  //     return false;
  //   }
  //   if (credentials.new_password !== credentials.confirm_password) {
  //     openErrorNotification("New password and confirm password does not match");
  //     return false;
  //   }
  //   if (!isPasswordStrong(credentials.new_password)) {
  //     openErrorNotification("Entered password is not strong");
  //     return false;
  //   }
  //   return true;
  // };

  const makeNewPassword = async () => {
    try {
      if (handleValidation()) {
        let captchaToken: any;
        if (urls.PROD) {
          captchaToken = await executeRecaptcha!();
        }
        setProcessing(true);
        const payloadBody = {
          ...user,
          pwd: credentials.new_password.value,
          captchaToken: captchaToken,
        };
        const token = user.authToken;
        await updatePwd(payloadBody, token);
        openSuccessNotification("Your password has been changed successfully");
        history.push(viewpaths.landingViewPath);
        setProcessing(false);
      }
    } catch (error: any) {
      setProcessing(false);
      openErrorNotification(error.message);
    }
  };

  const handleChange = (event: any) => {
    setCredentials({
      ...credentials,
      [event.target.name]: {
        value: event.target.value,
        error: "",
      },
    });
  };

  const passwordStrength = () => {
    const value = credentials.new_password.value;
    const { percentage, strength } = checkPasswordStrength(value);
    const filledSegments = percentage / 25;
    const requirements = [
      { label: "8+ characters", met: value.length >= 8 },
      { label: "A number", met: /\d/.test(value) },
      { label: "A symbol", met: /[^A-Za-z0-9]/.test(value) },
      {
        label: "Upper & lower case",
        met: /[a-z]/.test(value) && /[A-Z]/.test(value),
      },
    ];
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.75 }}>
          <Box sx={{ flex: 1, display: "flex", gap: 0.625 }}>
            {[0, 1, 2, 3].map((index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  height: 5,
                  borderRadius: "999px",
                  backgroundColor:
                    index < filledSegments
                      ? authRef.successBadgeIcon
                      : signer.border.light,
                }}
              />
            ))}
          </Box>
          <Typography
            sx={{
              fontSize: refFont.cardMeta,
              fontWeight: 700,
              color:
                strength === "Strong"
                  ? authRef.successBadgeIcon
                  : authRef.metaText,
            }}
          >
            {strength}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {requirements.map((requirement) => (
            <Box
              key={requirement.label}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.625,
                px: "10px",
                py: "4px",
                borderRadius: "999px",
                fontSize: refFont.legal,
                fontWeight: 600,
                color: requirement.met
                  ? authRef.successBadgeIcon
                  : authRef.metaText,
                backgroundColor: requirement.met
                  ? authRef.successBadgeBg
                  : signer.border.light,
              }}
            >
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M4.5 12.5l5 5L20 6.5" />
              </svg>
              {requirement.label}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      makeNewPassword();
    }
  };

  const renderNewPasswordScreen = () => {
    return (
      <Box
        component="main"
        id="main-content"
        tabIndex={-1}
        sx={{
          minHeight: "100vh",
          backgroundColor: authRef.pageBg,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 5,
          boxSizing: "border-box",
          outline: "none",
        }}
      >
        {/* Brand accent bar across the top of the canvas, per reference 3e. */}
        <Box
          aria-hidden="true"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: signerAccentBar,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        />

        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 470,
            borderRadius: "16px",
            border: `1px solid ${authRef.cardBorder}`,
            boxShadow: authRef.cardShadow,
            padding: { xs: "28px 24px 24px", sm: "40px 44px 36px" },
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
          }}
        >
          <Box
            component="img"
            src={inkfreeLogo}
            alt="Inkfree e-Sign"
            sx={{ width: 106, height: "auto", display: "block", mx: "auto" }}
          />
          <Typography
            component="h1"
            sx={{
              color: mobile.base[500],
              fontSize: refFont.cardHeading,
              fontWeight: 700,
              textAlign: "center",
              mt: 3,
            }}
          >
            Set your new password
          </Typography>
          <Typography
            sx={{
              color: authRef.subText,
              fontSize: refFont.cardBody,
              lineHeight: 1.6,
              textAlign: "center",
              mt: 1,
            }}
          >
            Make it strong — it guards your signatures.
          </Typography>

          <Box
            sx={{
              mt: 3.25,
              // Field chrome per reference 3e: 46px tall, 8px radius, pale
              // fill, 14px inset. `&&` outranks CustomInput's !important.
              "&& .MuiOutlinedInput-root": {
                minHeight: "46px",
                height: "46px",
                borderRadius: "8px",
                padding: "0 14px !important",
                backgroundColor: authRef.inputBg,
                fontSize: refFont.body,
                fontWeight: 500,
                color: mobile.base[500],
                "& fieldset": { borderColor: authRef.inputBorder },
                "&:hover fieldset": { borderColor: authRef.inputBorderHover },
              },
              "&& .MuiInputBase-input": { padding: "0 !important" },
              "&& .MuiInputBase-input::placeholder": {
                color: authRef.metaText,
                opacity: 1,
              },
              "&& .MuiInputLabel-root": {
                fontSize: refFont.label,
                fontWeight: 600,
                color: mobile.base[500],
                marginLeft: 0,
                "& .MuiFormLabel-asterisk": { color: signer.orange.main },
              },
            }}
          >
            <Stack spacing={2.25}>
              <CustomInput
                required
                autoFocus
                label="New password"
                placeHolder="New password"
                name="new_password"
                onChange={handleChange}
                type={visibility.new_password ? "text" : "password"}
                value={credentials.new_password.value}
                disabled={processing}
                onKeyPress={handleKeypress}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        setVisibility({
                          ...visibility,
                          new_password: !visibility.new_password,
                        })
                      }
                      aria-label={
                        visibility.new_password
                          ? "Hide password"
                          : "Show password"
                      }
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          setVisibility({
                            ...visibility,
                            new_password: !visibility.new_password,
                          });
                      }}
                    >
                      <SvgIcon>
                        {visibility.new_password ? (
                          <VisibilityOutlinedIcon />
                        ) : (
                          <VisibilityOffOutlinedIcon />
                        )}
                      </SvgIcon>
                    </InputAdornment>
                  ),
                }}
                propsToInputElement={{
                  "aria-required": true,
                  "aria-invalid": !!credentials.new_password.error,
                }}
                error={
                  !isTruthy(credentials.new_password.value) &&
                  credentials.new_password.error
                }
                helperText={
                  !isTruthy(credentials.new_password.value)
                    ? credentials.new_password.error
                    : ""
                }
              />
              {passwordStrength()}
              <CustomInput
                required
                label="Confirm password"
                placeHolder="Confirm password"
                name="confirm_password"
                onChange={handleChange}
                type={visibility.confirm_password ? "text" : "password"}
                value={credentials.confirm_password.value}
                onKeyPress={handleKeypress}
                disabled={processing}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="start"
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        setVisibility({
                          ...visibility,
                          confirm_password: !visibility.confirm_password,
                        })
                      }
                      aria-label={
                        visibility.confirm_password
                          ? "Hide password"
                          : "Show password"
                      }
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          setVisibility({
                            ...visibility,
                            confirm_password: !visibility.confirm_password,
                          });
                      }}
                    >
                      <SvgIcon>
                        {visibility.confirm_password ? (
                          <VisibilityOutlinedIcon />
                        ) : (
                          <VisibilityOffOutlinedIcon />
                        )}
                      </SvgIcon>
                    </InputAdornment>
                  ),
                }}
                propsToInputElement={{
                  "aria-required": true,
                  "aria-invalid": !!credentials.confirm_password.error,
                }}
                error={
                  !isTruthy(credentials.confirm_password.value) &&
                  credentials.confirm_password.error
                }
                helperText={
                  !isTruthy(credentials.confirm_password.value)
                    ? credentials.confirm_password.error
                    : ""
                }
              />
            </Stack>
          </Box>

          <Button
            fullWidth
            size="large"
            sx={{
              mt: 3.25,
              height: "48px",
              borderRadius: "8px",
              fontWeight: 700,
              textTransform: "none",
              fontSize: refFont.cardButton,
              letterSpacing: 0,
              outline: "none",
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: "2px",
              },
            }}
            type="submit"
            variant="contained"
            onClick={makeNewPassword}
            disabled={processing}
            aria-label="Set password and log in"
          >
            Set password &amp; log in
          </Button>
        </Card>

        <Typography
          sx={{
            mt: 2.5,
            fontSize: refFont.cardFooter,
            color: authRef.footerText,
            textAlign: "center",
          }}
        >
          © {new Date().getFullYear()} Inkfree. All rights reserved.
        </Typography>
      </Box>
    );
  };

  if (isLoggedIn) {
    history.push(authRoute);
    return null;
  }
  return renderNewPasswordScreen();
};

export default NewPassword;
